import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Smartphone, Users, BarChart3, QrCode, Shield, Zap, Check, X, 
  ChevronLeft, ChevronRight, ChevronDown, Instagram, Linkedin, Briefcase, Play, Link as LinkIcon,
  TrendingUp, Star
} from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';
import { PLAN_FEATURES, FEATURE_LABELS } from '../constants/subscriptions';
import type { SubscriptionPlan } from '../services/subscriptions';
import { usePageSEO } from '../hooks/usePageSEO';
import { LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis, CartesianGrid } from 'recharts';

const analyticsData = [
  { name: 'Mon', views: 400, clicks: 240 },
  { name: 'Tue', views: 300, clicks: 139 },
  { name: 'Wed', views: 550, clicks: 480 },
  { name: 'Thu', views: 450, clicks: 390 },
  { name: 'Fri', views: 700, clicks: 680 },
  { name: 'Sat', views: 600, clicks: 580 },
  { name: 'Sun', views: 800, clicks: 750 },
];

const LandingPage: React.FC = () => {
  const { pricing, supportEmail, loading: pricingLoading } = useConfig();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll effect for sticky navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqData = [
    {
      question: 'What is MY1QR?',
      answer: 'MY1QR lets you create a digital profile containing your links, social media, contact details, and payment options — all accessible through one QR code or shareable link.'
    },
    {
      question: 'Is MY1QR free to use?',
      answer: 'Yes. MY1QR offers a free plan with essential features. Premium plans unlock additional customization, analytics, and more link options.'
    },
    {
      question: 'How does my QR code work?',
      answer: 'Every MY1QR profile automatically generates a QR code. When someone scans it, they are instantly directed to your digital profile.'
    },
    {
      question: 'Can I receive payments through my profile?',
      answer: 'Yes. You can add your UPI details to receive direct payments from visitors — no extra apps or setup required.'
    },
    {
      question: 'How do I log in?',
      answer: 'You can log in using Email + Password, Email OTP, or WhatsApp OTP — whichever is most convenient for you.'
    },
    {
      question: 'Can I customize my profile?',
      answer: 'Yes. You can customize your profile with your photo, bio, links, social media accounts, and communities you belong to.'
    },
  ];

  // FAQ JSON-LD structured data for SEO
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-injected', 'true');
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.querySelectorAll('script[data-seo-injected]').forEach((el) => el.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEO for landing page
  usePageSEO({
    title:       'MY1QR – Your Digital Identity Hub',
    description: 'Create your personalised digital business card. Share links, UPI payments, social profiles and more — all from one QR code.',
    keywords:    'digital business card, QR code, link in bio, MY1QR, myoneqr, digital card India',
    ogImage:     'https://myoneqr.com/og-banner.png',
    ogUrl:       'https://myoneqr.com',
    canonical:   'https://myoneqr.com',
    type:        'website',
  });

  const slides = [
    { id: 'profile', title: 'Digital Profiles' },
    { id: 'analytics', title: 'Deep Analytics' },
    { id: 'leads', title: 'Lead Generation' },
    { id: 'qr', title: 'Smart QR Codes' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const formatPrice = (plan: SubscriptionPlan, period: 'monthly' | 'yearly') => {
    if (plan === 'trial') return { primary: 'Included for 15 days', secondary: 'Auto-activated on first login' };
    if (plan === 'free') return { primary: '₹0', secondary: 'Forever free mode' };
    
    const amount = pricing?.[plan]?.[period];
    if (amount === undefined || amount === null) return { primary: '—', secondary: '' };
    
    return {
      primary: `₹${amount}`,
      secondary: `per ${period === 'monthly' ? 'month' : 'year'}`,
      tertiary: period === 'monthly' && pricing?.[plan]?.yearly
        ? `or ₹${pricing[plan].yearly}/year`
        : period === 'yearly' && pricing?.[plan]?.monthly
          ? `or ₹${pricing[plan].monthly}/month`
          : '',
    };
  };

  const getPlanDescription = (plan: SubscriptionPlan) => {
    switch (plan) {
      case 'trial': return 'Full access to every feature for your first 15 days.';
      case 'free': return 'Stay visible online with basic profile and public page access.';
      case 'basic': return 'Unlock social and custom links to grow your presence.';
      case 'premium': return 'Everything in Basic plus payment integrations.';
      default: return '';
    }
  };

  const allFeatures = Object.keys(FEATURE_LABELS);
  const plans: SubscriptionPlan[] = ['trial', 'free', 'basic', 'premium'];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900">
      
      {/* Dynamic Sticky Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/80 backdrop-blur-lg border-b border-gray-100 py-3 shadow-sm' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3 active:scale-95 transition-transform">
              <img src="/logo.png" alt="MY1QR" className="h-9 w-auto" />
              <span className="text-2xl font-bold tracking-tight text-indigo-600">MyOneQR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-1">
              {['Features', 'Analytics', 'Pricing', 'FAQ'].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-full transition-all duration-200"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(`#${item.toLowerCase()}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {item}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-1 sm:gap-3">
              <Link to="/login" className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-indigo-600 bg-indigo-50/80 hover:bg-indigo-600 hover:text-white rounded-full transition-all duration-300 active:scale-95 whitespace-nowrap border border-indigo-100/50">
                Login
              </Link>
              <Link to="/signup" className="px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-full shadow-[0_4px_14px_0_rgba(79,70,229,0.1)] hover:shadow-[0_4px_20px_0_rgba(79,70,229,0.2)] hover:-translate-y-0.5 transition-all duration-300 active:scale-95 whitespace-nowrap">
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-28 lg:pt-48 lg:pb-40 overflow-hidden bg-gradient-to-b from-slate-50 via-indigo-50/50 to-slate-200/50 border-b border-slate-200 shadow-[inset_0_-20px_40px_rgba(0,0,0,0.02)]">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-[0.03] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-8 items-center">
            
            {/* Left Content */}
            <div className="text-center lg:text-left animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-semibold tracking-wide uppercase mb-8 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                </span>
                The Modern Digital Identity
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6">
                One Link. <br/>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-500">
                  Endless Growth.
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-light">
                Consolidate your links, capture leads, accept UPI payments, and showcase your professional portfolio with a single, beautiful QR code and profile link.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/signup"
                  className="px-8 py-4 text-base font-semibold text-white bg-indigo-600 rounded-full shadow-[0_8px_30px_rgb(79,70,229,0.3)] hover:shadow-[0_8px_40px_rgb(79,70,229,0.4)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 group active:scale-95"
                >
                  Start Building Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button 
                  onClick={() => setShowVideoModal(true)}
                  className="px-8 py-4 text-base font-semibold text-slate-700 bg-white border border-slate-200 rounded-full hover:bg-slate-50 hover:border-slate-300 hover:-translate-y-1 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center gap-2 active:scale-95"
                >
                  <Play className="w-4 h-4 text-indigo-600 fill-indigo-600" />
                  Watch Demo
                </button>
              </div>
            </div>

            {/* Right Side QR Carousel Layout exactly preserved as requested! */}
            <div className="relative animate-float">
              <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-8 max-w-sm mx-auto relative group overflow-hidden">
                {/* Carousel Navigation */}
                <button
                  onClick={() => setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm p-1.5 rounded-full shadow-md text-gray-600 hover:text-primary-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentSlide(prev => (prev + 1) % slides.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 backdrop-blur-sm p-1.5 rounded-full shadow-md text-gray-600 hover:text-primary-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Slides Container */}
                <div className="relative h-[420px] w-full preserve-3d">
                  {/* Slide 1: Profile */}
                  <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${currentSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                    <div className="h-full bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white text-center flex flex-col items-center justify-center">
                      <div className="w-20 h-20 bg-white/20 rounded-full mb-4 flex items-center justify-center backdrop-blur-sm">
                        <Smartphone className="h-10 w-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-1 text-white">Dzyte Services</h3>
                      <p className="text-primary-100 mb-6 text-sm">IT Support</p>
                      <div className="space-y-3 w-full max-w-[240px]">
                        <div className="bg-white/20 rounded-xl p-3 flex items-center shadow-sm">
                          <div className="w-8 h-8 bg-white/30 rounded-full mr-3 flex items-center justify-center">
                            <Instagram className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium">Instagram</span>
                        </div>
                        <div className="bg-white/20 rounded-xl p-3 flex items-center shadow-sm">
                          <div className="w-8 h-8 bg-white/30 rounded-full mr-3 flex items-center justify-center">
                            <Linkedin className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium">LinkedIn</span>
                        </div>
                        <div className="bg-white/20 rounded-xl p-3 flex items-center shadow-sm">
                          <div className="w-8 h-8 bg-white/30 rounded-full mr-3 flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium">Portfolio</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Slide 2: Analytics */}
                  <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${currentSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                    <div className="h-full bg-white border border-gray-100 shadow-inner rounded-2xl p-6 flex flex-col">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-800">Analytics</h3>
                        <div className="p-2 bg-primary-50 rounded-lg">
                          <BarChart3 className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <p className="text-xs text-gray-500 mb-1">Total Views</p>
                          <p className="text-xl font-bold text-primary-600">12.4k</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <p className="text-xs text-gray-500 mb-1">Link Clicks</p>
                          <p className="text-xl font-bold text-green-600">3.2k</p>
                        </div>
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-xl border border-gray-100 p-4 flex items-end justify-between gap-2 md:gap-3">
                        <div className="w-full bg-primary-200 rounded-t-md hover:bg-primary-300 transition-colors" style={{ height: '40%' }}></div>
                        <div className="w-full bg-primary-300 rounded-t-md hover:bg-primary-400 transition-colors" style={{ height: '60%' }}></div>
                        <div className="w-full bg-primary-400 rounded-t-md hover:bg-primary-500 transition-colors" style={{ height: '35%' }}></div>
                        <div className="w-full bg-primary-500 rounded-t-md hover:bg-primary-600 transition-colors" style={{ height: '80%' }}></div>
                        <div className="w-full bg-primary-600 rounded-t-md hover:bg-primary-700 transition-colors" style={{ height: '100%' }}></div>
                        <div className="w-full bg-primary-400 rounded-t-md hover:bg-primary-500 transition-colors" style={{ height: '50%' }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Slide 3: Leads */}
                  <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${currentSlide === 2 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                    <div className="h-full bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white flex flex-col">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-bold text-white">Recent Leads</h3>
                          <p className="text-xs text-slate-400">Captured from profile</p>
                        </div>
                        <div className="p-2 bg-white/10 rounded-lg">
                          <Users className="h-5 w-5 text-white/90" />
                        </div>
                      </div>
                      <div className="space-y-3 flex-1 overflow-hidden">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="bg-white/10 rounded-xl p-3 flex items-center gap-3 backdrop-blur-sm border border-white/5 hover:bg-white/15 transition-colors cursor-default">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-inner">
                              <span className="font-bold text-sm text-white">{(['D', 'S', 'M', 'K'])[i - 1]}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate text-white">{['Divya Natarajan', 'Suriya Krishnan', 'Manoj Kumar', 'Kavitha Rajan'][i - 1]}</p>
                              <p className="text-xs text-slate-300 truncate">{['divya.n@example.com', 'suriya.k@design.co', 'manoj.k@dev.io', 'kavitha.r@mail.com'][i - 1]}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Slide 4: QR Codes */}
                  <div className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${currentSlide === 3 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                    <div className="h-full bg-white border border-gray-100 shadow-inner text-center rounded-2xl flex flex-col relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-32 bg-primary-50 rounded-t-2xl"></div>
                      <div className="flex-1 flex flex-col justify-center items-center p-6 mt-4">
                        <div className="relative z-10 bg-white p-4 rounded-3xl shadow-xl border border-gray-100 mb-6 group cursor-default">
                          <QrCode className="w-32 h-32 text-gray-800" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 relative z-10">Scan to Connect</h3>
                        <p className="text-sm text-gray-500 relative z-10 mb-6">Download custom QR codes for offline promotion</p>
                        <div className="w-full bg-primary-50 text-primary-700 py-3 rounded-xl text-sm font-bold shadow-sm relative z-10">
                          Ready to Download
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Indicators */}
                <div className="flex justify-center gap-2 mt-6">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${currentSlide === idx ? 'w-6 bg-primary-600' : 'w-2 bg-gray-200 hover:bg-gray-300'}`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Feature Section with Staggered Hover Cards */}
      <section id="features" className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-24">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Features engineered for <span className="text-indigo-600">performance</span>
            </h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto font-light leading-relaxed">
              We abstracted all the complexity of hosting, analytics, and contact capturing so you can focus purely on sharing your best self.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: Smartphone, title: 'Adaptive Mobile Design', p: 'Instantly beautifully formatted on any smartphone aspect ratio without pixel clipping.' },
              { icon: LinkIcon, title: 'Unlimited Smart Links', p: 'Add your portfolio, e-commerce stores, social pages, and video embeds all in one view.' },
              { icon: Users, title: 'Rich Collaboration', p: 'Highlight teams, sponsors, or organizations you are affiliated with using custom badging.' },
              { icon: BarChart3, title: 'Real-time Analytics', p: 'See exactly who clicked what, where traffic originates, and what devices are used.' },
              { icon: Shield, title: 'Enterprise Security', p: 'Your profile visitor data is absolutely locked down using strictly enforced privacy rules.' },
              { icon: Zap, title: 'Edge Network Speed', p: 'Assets are pre-cached across global edge networks ensuring split-second load times.' }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="group relative bg-slate-50/50 border border-slate-100 p-8 rounded-3xl hover:-translate-y-2 hover:bg-white hover:border-indigo-100 hover:shadow-[0_20px_40px_-15px_rgba(79,70,229,0.1)] transition-all duration-300"
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-slate-100 group-hover:scale-110 group-hover:bg-indigo-50 transition-all duration-300">
                  <feature.icon className="h-6 w-6 text-slate-700 group-hover:text-indigo-600 transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed font-light">{feature.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Interactive Preview */}
      <section id="analytics" className="py-24 bg-slate-50 border-y border-slate-200 shadow-[inset_0_20px_40px_rgba(0,0,0,0.01)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-3xl blur-3xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-white border border-slate-100 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] rounded-3xl p-6 lg:p-10 transform transition-transform duration-500 hover:-translate-y-2">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h4 className="text-xl font-bold text-slate-900">Traffic Overview</h4>
                    <p className="text-sm text-slate-500">Your profile performance this week</p>
                  </div>
                  <div className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-full text-sm font-bold flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    +24%
                  </div>
                </div>
                <div className="h-[250px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} dy={10} />
                      <YAxis hide={true} />
                      <RechartsTooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                        cursor={{ stroke: '#E2E8F0', strokeWidth: 1 }}
                      />
                      <Line type="monotone" dataKey="views" stroke="#4F46E5" strokeWidth={3} dot={false} activeDot={{r: 6, fill: '#4F46E5', stroke: '#fff', strokeWidth: 2}} animationDuration={1500} />
                      <Line type="monotone" dataKey="clicks" stroke="#A855F7" strokeWidth={3} dot={false} activeDot={{r: 6, fill: '#A855F7', stroke: '#fff', strokeWidth: 2}} animationDuration={2000} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
                Understand your audience with pinpoint precision.
              </h2>
              <p className="text-xl text-slate-500 font-light leading-relaxed mb-8">
                Stop guessing. Our beautiful charting dashboards show you exactly which links convert the most, where visitors are geographically, and what devices they use in real-time.
              </p>
              <ul className="space-y-4 mb-8">
                {['Track exact CTR on every custom module', 'Discover top geography and referrals', 'Export to PDF/CSV instantly'].map((item, i) => (
                  <li key={i} className="flex items-center text-slate-700">
                    <Check className="w-5 h-5 text-indigo-500 mr-3 flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="group font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors">
                Explore Analytics <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto font-light">
              No hidden fees. Just choose the tier that fits your growth stage.
            </p>
          </div>

          <div className="flex justify-center mb-16">
            <div className="bg-slate-100 p-1.5 rounded-full inline-flex relative shadow-inner">
              <div 
                className={`absolute inset-y-1.5 w-[calc(50%-6px)] bg-white rounded-full shadow-sm transition-transform duration-300 ease-out ${billingPeriod === 'monthly' ? 'translate-x-0' : 'translate-x-full'}`} 
              />
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`relative px-8 py-2.5 text-sm font-semibold rounded-full w-32 transition-colors duration-300 z-10 ${billingPeriod === 'monthly' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`relative px-8 py-2.5 text-sm font-semibold rounded-full w-32 transition-colors duration-300 z-10 ${billingPeriod === 'yearly' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Yearly
              </button>
            </div>
            {billingPeriod === 'monthly' && pricing?.premium?.yearly && (
              <div className="absolute -mt-6 ml-44 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse">Save 20%</div>
            )}
          </div>

          {pricingLoading ? (
            <div className="flex justify-center pb-24"><div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {plans.map((plan) => {
                const isPremium = plan === 'premium';
                const pInfo = formatPrice(plan, billingPeriod);

                return (
                  <div key={plan} className={`relative bg-white rounded-3xl p-6 lg:p-8 transition-transform duration-300 ${isPremium ? 'border-2 border-indigo-500 shadow-2xl shadow-indigo-500/10 scale-100 lg:scale-105 z-10' : 'border border-slate-200 shadow-lg shadow-slate-200/50 hover:-translate-y-1 z-0'}`}>
                    {isPremium && (
                      <div className="absolute -top-4 inset-x-0 flex justify-center">
                        <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">Most Popular</span>
                      </div>
                    )}
                    {plan === 'trial' && (
                      <div className="absolute -top-4 inset-x-0 flex justify-center">
                        <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">Try Free</span>
                      </div>
                    )}
                    
                    <h3 className="text-xl font-bold text-slate-900 capitalize mb-2">{plan === 'trial' ? 'Trial Plan' : plan}</h3>
                    <p className="text-slate-500 text-sm h-12 lg:h-10">{getPlanDescription(plan)}</p>
                    
                    <div className="my-8">
                      <span className={`${plan === 'trial' ? 'text-2xl lg:text-3xl' : 'text-5xl'} font-bold text-slate-900 tracking-tight`}>{pInfo.primary}</span>
                      {pInfo.secondary && <span className={`${plan === 'trial' ? 'text-sm block mt-2 text-slate-500' : 'text-slate-500 ml-2'}`}>{pInfo.secondary}</span>}
                      {pInfo.tertiary && <div className="text-sm text-green-600 font-medium mt-1">{pInfo.tertiary}</div>}
                    </div>

                    <Link
                      to={`/signup?plan=${plan}`}
                      className={`block w-full text-center py-4 rounded-xl font-bold transition-all duration-300 mb-8 active:scale-95 ${
                        isPremium 
                          ? 'bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 hover:shadow-indigo-500/25' 
                          : plan === 'trial' 
                            ? 'bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 hover:shadow-emerald-500/25'
                            : 'bg-slate-50 text-slate-900 hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      {plan === 'trial' ? 'Start Free Trial' : plan === 'free' ? 'Get Started' : `Choose ${plan}`}
                    </Link>

                    <ul className="space-y-4">
                      {allFeatures.map((f, i) => {
                        const included = PLAN_FEATURES[plan].includes(f as any);
                        return (
                          <li key={i} className="flex items-center text-sm">
                            {included ? <Check className="w-5 h-5 text-indigo-500 mr-3 shrink-0" /> : <X className="w-5 h-5 text-slate-300 mr-3 shrink-0" />}
                            <span className={included ? 'text-slate-700' : 'text-slate-400'}>{FEATURE_LABELS[f]}</span>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#FAFAFA] border-y border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">Loved by creators</h2>
          <p className="text-xl text-slate-500 font-light">Join thousands building their custom presence.</p>
        </div>
        
        {/* Horizontal scroll track */}
        <div className="flex space-x-6 px-4 md:px-16 overflow-x-auto snap-x snap-mandatory pb-12 [&::-webkit-scrollbar]:hidden w-full max-w-[100vw]">
          {[
            { img: 'https://i.pravatar.cc/150?u=12', name: 'Priya Sharma', role: 'Content Creator', quote: 'MY1QR helped me consolidate all my social media links into one beautiful profile. My engagement skyrocketed overnight.' },
            { img: 'https://i.pravatar.cc/150?u=32', name: 'Rahul Verma', role: 'Startup Founder', quote: 'We moved our entire team\'s email signature cards over. The analytics show us exactly who is checking out our portfolio.' },
            { img: 'https://i.pravatar.cc/150?u=53', name: 'Arjun Kapoor', role: 'Freelance Designer', quote: 'The ability to drop my payment QR right next to my Behance portfolio has sped up client closing by days.' },
            { img: 'https://i.pravatar.cc/150?u=44', name: 'Neha Desai', role: 'Marketing Lead', quote: 'I have tested every link-in-bio tool on the market. This is by far the fastest and has the absolute cleanest UX.' },
          ].map((t, i) => (
            <div key={i} className="snap-center shrink-0 w-[85vw] sm:w-[400px] bg-white border border-slate-100 rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all duration-300">
              <div className="flex items-center mb-6">
                {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-amber-400 text-amber-400 mr-1" />)}
              </div>
              <p className="text-slate-700 text-lg mb-8 leading-relaxed font-light">"{t.quote}"</p>
              <div className="flex items-center">
                <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full mr-4 border border-slate-200" />
                <div>
                  <h4 className="font-bold text-slate-900">{t.name}</h4>
                  <p className="text-sm text-slate-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">Got questions?</h2>
          </div>
          <div className="space-y-4">
            {faqData.map((item, i) => (
              <div key={i} className={`border ${openFaq === i ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-200 bg-white'} rounded-2xl transition-colors duration-300`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none group active:bg-slate-50 rounded-2xl"
                >
                  <span className="text-lg font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.question}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${openFaq === i ? 'bg-indigo-600 rotate-180 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'}`}>
                    <ChevronDown className="w-5 h-5" />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${openFaq === i ? 'max-h-96 opacity-100 pb-6 px-6' : 'max-h-0 opacity-0 px-6'}`}>
                  <p className="text-slate-600 leading-relaxed pt-2 border-t border-slate-200/60">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Large Bottom CTA */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-900 rounded-[3rem] p-10 md:p-20 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-indigo-500 rounded-full blur-[80px] opacity-40 mix-blend-screen"></div>
            <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-64 h-64 bg-purple-500 rounded-full blur-[80px] opacity-40 mix-blend-screen"></div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-6 mt-4 relative z-10">
              Ready to stand out?
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto font-light relative z-10">
              Stop losing links and scattering your online footprint. Build your unified digital identity in under 2 minutes today.
            </p>
            <Link
              to="/signup"
              className="relative z-10 px-10 py-5 text-lg font-bold text-slate-900 bg-white rounded-full shadow-[0_0_40px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_rgba(255,255,255,0.5)] hover:-translate-y-1 transition-all duration-300 inline-flex items-center gap-2 group active:scale-95"
            >
              Get Started For Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img src="/logo.png" alt="MY1QR" className="h-8 w-auto" />
                <span className="text-xl font-bold text-indigo-600">MyOneQR</span>
              </div>
              <p className="text-slate-500 max-w-sm leading-relaxed mb-6 font-light">
                The modern standard for digital identities. Connect everything you are into a single responsive hub.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-6 text-slate-900 tracking-wide uppercase text-xs">Platform</h4>
              <ul className="space-y-4 text-slate-500">
                <li><a href="#features" className="hover:text-indigo-600 transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-indigo-600 transition-colors">Pricing</a></li>
                <li><a href="#faq" className="hover:text-indigo-600 transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-6 text-slate-900 tracking-wide uppercase text-xs">Legal & Support</h4>
              <ul className="space-y-4 text-slate-500">
                <li><Link to="/terms" className="hover:text-indigo-600 transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link></li>
                {supportEmail && <li><a href={`mailto:${supportEmail}`} className="hover:text-indigo-600 transition-colors">{supportEmail}</a></li>}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-slate-400 text-sm">
            <p>&copy; {new Date().getFullYear()} MyOneQR. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Video Modal - preserved */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md" onClick={() => setShowVideoModal(false)}>
          <div className="relative w-full max-w-5xl bg-black rounded-3xl overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)] transform transition-transform" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowVideoModal(false)} className="absolute top-4 right-4 z-[110] p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-full max-h-[85vh] flex items-center justify-center">
              <video src="/demo.mp4" controls autoPlay className="w-full h-full max-h-[85vh] object-contain">
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
